<script lang="ts">
    import type { Models } from '@smartlinkdev/types';
    import { onMount } from 'svelte';
    import { goto } from '$app/navigation';
    import {
      forceRoveMap,
      mapInstance,
      venue,
      showDirections,
      destinationId,
      enableDirections,
      currentLocation,
      expandedLocation,
    } from '$lib/stores';
    import { t } from '$lib/translations';
    import {
      calculateFullSize,
      formatLocationUrl,
      processDescription,
      processLocationHours,
    } from '$lib/utils/location';

    export let location: Models.Location;
    let venueSeasonal = $venue.settings?.api?.venueDisplaySeasonalLocations || false;
    const weekdays = [
      $t('content.weekdays.sun'),
      $t('content.weekdays.mon'),
      $t('content.weekdays.tue'),
      $t('content.weekdays.wed'),
      $t('content.weekdays.thu'),
      $t('content.weekdays.fri'),
      $t('content.weekdays.sat'),
    ];

    let fullSize = 350;

    // These are needed for rove maps tooltips to function:
    let closeBtn: any = undefined;
    let closeBtnListening = false;
    let directionsBtn: any = undefined;
    let directionsBtnListening = false;
    let expandBtn: any = undefined;
    let expandBtnListening = false;
    let detailsBtn: any = undefined;
    let detailsBtnListening = false;
    let locationBtn: any = undefined;
    let locationBtnListening = false;
    let urlBtn: any = undefined;
    let urlBtnListening = false;

    let hideBannerImg = $venue.settings?.embed?.details?.hideBannerImg || false;
    let hideLogoImg = $venue.settings?.embed?.details?.hideLogoImg || false;
    let hidePhone = $venue.settings?.embed?.details?.hidePhone || false;
    let hideHours = $venue.settings?.embed?.details?.hideHours || false;
    let hideAddress = $venue.settings?.embed?.details?.hideAddress || false;
    let hideWebsite = $venue.settings?.embed?.details?.hideWebsite || false;
    let startExpanded = $venue.settings?.embed?.details?.startExpanded || false;
    let roveMaps = $venue.settings?.mapType === 'rove' || $forceRoveMap;

    let url = formatLocationUrl(location.url);
    const { groupedHours } = processLocationHours(location, venueSeasonal);

    // These are needed for rove maps tooltips to function:
    $: if (roveMaps && locationBtn && !locationBtnListening) {
        locationBtnListening = true;
        locationBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
        });
        locationBtn.addEventListener('wheel', (e) => {
            e.stopPropagation();
        });
    }

    $: if (roveMaps && urlBtn && !urlBtnListening) {
        urlBtnListening = true;
        urlBtn.addEventListener('click', (e: MouseEvent): void => {
            e.preventDefault();
            e.stopPropagation();
            window.open(url, '_blank')?.focus();
        });
    }

    $: if (roveMaps && closeBtn && !closeBtnListening) {
        closeBtnListening = true;
        closeBtn.addEventListener('click', (e: MouseEvent): void => {
            e.preventDefault();
            e.stopPropagation();
            const el = document.getElementById('location-details');
            if (el) el.remove();

            if ($mapInstance) {
                if (location.id) {
                    $mapInstance.removeLocationHighlight(location.id);
                }
            }
            goto(`/venues/${$venue.id}`);
        });
    }

    $: if (roveMaps && expandBtn && !expandBtnListening) {
        expandBtnListening = true;
        expandBtn.addEventListener('click', (e: MouseEvent): void => {
            e.preventDefault();
            e.stopPropagation();
            const fullDetails = document.getElementById('full-details');
            const bannerImage = document.getElementById('bannerImage');
            if (fullDetails) {
                const expanded = fullDetails.dataset.expanded;
                if (expanded === 'false') {
                    fullDetails.style.maxHeight = '400px';
                    fullDetails.dataset.expanded = 'true';
                    if (bannerImage) {
                        bannerImage.style.display = 'none';
                    }
                } else {
                    fullDetails.style.maxHeight = '0px';
                    fullDetails.dataset.expanded = 'false';
                }
            }
        });

    }

    $: if (roveMaps && detailsBtn && !detailsBtnListening) {
        detailsBtnListening = true;
        detailsBtn.addEventListener('click', (e: MouseEvent): void => {
            e.preventDefault();
            e.stopPropagation();
            $expandedLocation = location;
            const el = document.getElementById('location-details');
            if (el) {
              el.style.display = 'none';
            }
        });
    }

    $: if (roveMaps && directionsBtn && !directionsBtnListening) {
        directionsBtnListening = true;
        directionsBtn.addEventListener('click', (e: MouseEvent): void => {
            $showDirections = true;
            $destinationId = `location|${location.id}`;
        });
    }

    let processedDescription: string;
    $: processedDescription = processDescription(location);

    onMount(async () => {
        if ($venue.settings.mapType === 'mapbox' && !$forceRoveMap) {
            const locDetails = document.getElementById('location-details');
            if (locDetails) {
                locDetails.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                });
                locDetails.addEventListener('wheel', (e) => {
                    e.stopPropagation();
                });
            }
            fullSize = calculateFullSize(location);

            const directionsBtn = document.getElementById('directions-btn');
            if (directionsBtn) {
                directionsBtn.addEventListener('click', (e: MouseEvent): void => {
                    $showDirections = true;
                    $destinationId = `location|${location.id}`;
                });
            }

            const urlBtn = document.getElementById('details-url');
            if (urlBtn) {
                urlBtn.addEventListener('click', (e: MouseEvent): void => {
                    window.open(url, '_blank')?.focus();
                });
            }

            const closeButton = document.getElementById('close-btn');
            if (closeButton) {
                closeButton.addEventListener('pointerdown', (e: MouseEvent): void => {
                    e.preventDefault();
                    e.stopPropagation();
                    const el = document.getElementById('location-details');
                    if (el) {
                      el.style.display = 'none';
                    }

                    if ($mapInstance) {
                        $currentLocation = undefined;
                        $mapInstance.removeHighlights();
                    }
                    goto(`/venues/${$venue.id}`);
                });
            }

            const expandButton = document.getElementById('expand-btn');
            if (expandButton) {
                expandButton.addEventListener('click', (e: MouseEvent): void => {
                    const fullDetails = document.getElementById('full-details');
                    const bannerImage = document.getElementById('bannerImage');
                    if (fullDetails) {
                        const expanded = fullDetails.dataset.expanded;
                        if (expanded === 'false') {
                            fullDetails.style.maxHeight = `${fullSize}px`;
                            fullDetails.dataset.expanded = 'true';
                            fullDetails.style.overflowY = 'hidden';
                            if (bannerImage) {
                                bannerImage.style.display = 'none';
                            }
                        } else {
                            fullDetails.style.maxHeight = '0px';
                            fullDetails.dataset.expanded = 'false';
                        }
                    }
                });
            }

            const detailsButton = document.getElementById('details-btn');
            if (detailsButton) {
                detailsButton.addEventListener('click', (e: MouseEvent): void => {
                    $expandedLocation = location;
                    const el = document.getElementById('location-details');
                    if (el) {
                      el.style.display = 'none';
                    }
                });
            }

            if (startExpanded) {
                const fullDetails = document.getElementById('full-details');
                if (fullDetails) {
                    fullDetails.style.maxHeight = `${fullSize}px`;
                    fullDetails.dataset.expanded = 'true';
                }
            }
        }
    });
</script>

<div class="location-details-tooltip" role="tooltip" id="location-details" bind:this={locationBtn}>
  <div class="grid grid-cols-[20px_1fr_20px] place-items-center">
    <div id="expand-btn" bind:this={expandBtn}>
      <span class="material-symbols-outlined cursor-pointer">pan_zoom</span>
    </div>
    <div class="flex-grow text-center px-2 text-xl font-bold text-gray-500">
      {location.name}
    </div>
    <div id="close-btn" bind:this={closeBtn}>
      <span class="material-symbols-outlined cursor-pointer">cancel</span>
    </div>
  </div>
  <div id="full-details" class="location-details-inner" data-expanded="false">
    {#if location.location_types}
      {#each location.location_types.filter((c) => c.highlight) as category}
        <div class="highlight-category">{category.name}</div>
      {/each}
    {/if}
    {#if location.logo_img && !hideLogoImg}
      <div class="location-logo-img w-full max-h-24 grid place-items-center my-4">
        <img src={location.logo_img} class="max-h-24" style="object-fit: contain;"/>
      </div>
    {/if}
    <div id="more-details" data-expanded="false" class="mt-2">
      {#if location.phone && !hidePhone}
        <h3>{ $t('content.phoneNumber') }</h3>
        <div class="details-piece">{location.phone}</div>
      {/if}
      {#if location.url && !hideWebsite}
        <h3>{ $t('content.website') }</h3>
        <div class="details-piece cursor-pointer" id="details-url" bind:this={urlBtn}>
          {location.url}
        </div>
      {/if}
      {#if !hideHours && (location.hours || groupedHours)}
        <h3>{ $t('content.hours') } <span class="text-gray-500">{ $t('content.hoursMayVary') }</span></h3>
        <div class="details-piece">
          {#if groupedHours}
            <div class="grid grid-cols-[150px_1fr]">
              {#each Object.keys(groupedHours) as day_number }
                <div>
                  <b>{weekdays[day_number]}</b>
                </div>
                <div>
                  {#each groupedHours[day_number] as entry}
                    <div>{entry.open_time_formatted} - {entry.close_time_formatted}</div>
                  {/each}
                </div>
              {/each}
            </div>
          {:else}
            {@html location.hours.replaceAll('\n', '<br />') }
          {/if}
        </div>
      {/if}
      {#if !hideAddress && location.address1}
        <h3>Address</h3>
        <div class="details-piece">
          {location.address1}{ location.city ? `, ${location.city}` : '' }{ location.state ? `, ${location.state}` : '' } { location.zip ? `${location.zip}` : '' }
        </div>
      {/if}
      <h3>{ $t('content.description') }</h3>
    </div>
    <div class="details-description">
      <div>
        {@html processedDescription}
      </div>
    </div>
    {#if location.banner_img && !hideBannerImg}
      <div id="bannerImage" class="location-logo-img w-full grid place-items-center my-4 hidden">
        <img src={location.banner_img} class="max-h-48" style="object-fit: contain; border-radius: 0.8vh"/>
      </div>
    {/if}
    {#if $enableDirections}
    <div class="grid grid-cols-[50%_50%] gap-1 mt-4">
      <div class="mx-1">
        <button id="details-btn" class="bottom-button more-button" bind:this={detailsBtn}>
          { $t('content.moreInfo') }
        </button>
      </div>
      <div class="mx-1">
        <button id="directions-btn" class="bottom-button directions-button" bind:this={directionsBtn}>
          { $t('content.getDirections') }
        </button>
      </div>
    </div>
    {:else}
      <div class="grid grid-cols-[100%] gap-1 mt-4">
        <div class="mx-1">
          <button id="details-btn" class="bottom-button more-button" bind:this={detailsBtn}>
            { $t('content.moreInfo') }
          </button>
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
    .location-details-tooltip {
        @apply text-sm font-medium bg-white rounded-2xl shadow-sm px-6 py-4 text-gray-600 drop-shadow-lg mb-40 hidden md:inline-block;
        width: 400px;
        pointer-events: auto;
        z-index: 10001;
        position: absolute;
        top: 20px;
        left: -200px;
        transform-origin: top;
    }

    .location-details-inner {
        max-height: 0px;
        overflow-y: auto;
        transition: max-height 1s cubic-bezier(0, 1, 0, 1);
        transform-origin: top;
        will-change: max-height;
    }

    .more-button {
      background-color: #ed736f;
    }

    .directions-button {
      background-color: #475883;
    }

    .bottom-button {
        @apply text-white text-center py-2 w-full rounded-2xl;
    }

    #more-details {
        max-height: 0px;
        overflow-y: hidden;
        transition: max-height 2s cubic-bezier(0, 1, 0, 1);
        transform-origin: top;
        will-change: max-height;
    }

    .location-details-tooltip h3 {
        @apply font-bold text-lg mb-1;
    }

    .details-piece {
        @apply mb-6;
    }

    .details-description {
        max-height: 110px;
        overflow-y: auto;
        padding-right: 4px;
        --sb-track-color: #ccc;
        --sb-thumb-color: #aaa;
        --sb-size: 8px;
    }

    :global(.highlight-category) {
        @apply my-1 p-2 rounded-xl w-[98%] text-white bg-gray-300 text-center font-bold;
    }
</style>
